import { useState, useEffect, useMemo } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, Column, Modal, Input, Label, DeleteConfirmDialog } from '@/components/ui';
import { ResponsiveTable as DataTable } from '@/components/ui/responsive-table';
import {
  Plus,
  Loader2,
  Network,
  Globe,
  Trash2,
  Edit,
  Flag,
  Hash,
  AlertCircle,
  Signal,
  MapPin,
  ExternalLink,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { Network as NetworkType } from '@simcheck/shared-types';
import { useToast } from '@/contexts/ToastContext';
import { useDataTableFilters, DataTableFilter, createColumnConfigHelper, applyFilters } from '@/components/data-table-filter';
import { edgeDeviceService } from '@/services/edgeDeviceService';

// Helper to convert Firestore Timestamp or ISO string to Date
function toDate(value: any): Date | undefined {
  if (!value) return undefined;
  if (value.toDate && typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (typeof value === 'string') {
    return new Date(value);
  }
  if (value instanceof Date) {
    return value;
  }
  return undefined;
}

// Extended network type with availability data
interface NetworkWithAvailability extends NetworkType {
  availability?: {
    totalModemCount: number;
    totalLocationCount: number;
    isStale: boolean;
    locationIds: string[];
  };
}

// Filter column helper
const filterColumnHelper = createColumnConfigHelper<NetworkType>();

// Static filter configs
const getStaticFilterConfigs = () => [
  filterColumnHelper
    .text()
    .id('name')
    .accessor((row) => row.name)
    .displayName('Network Name')
    .icon(Network)
    .build(),
  filterColumnHelper
    .text()
    .id('mcc')
    .accessor((row) => row.mcc)
    .displayName('MCC')
    .icon(Hash)
    .build(),
  filterColumnHelper
    .text()
    .id('mnc')
    .accessor((row) => row.mnc)
    .displayName('MNC')
    .icon(Hash)
    .build(),
  filterColumnHelper
    .option()
    .id('isGlobal')
    .accessor((row) => String(row.isGlobal))
    .displayName('Type')
    .icon(Globe)
    .options([
      { label: 'Global', value: 'true' },
      { label: 'Custom', value: 'false' },
    ])
    .build(),
  filterColumnHelper
    .option()
    .id('isActive')
    .accessor((row) => String(row.isActive))
    .displayName('Status')
    .icon(Network)
    .options([
      { label: 'Active', value: 'true' },
      { label: 'Inactive', value: 'false' },
    ])
    .build(),
];

export function NetworksPage() {
  const { currentUser, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  // Data state
  const [networks, setNetworks] = useState<NetworkType[]>([]);
  // Network waiting for confirmation in the DeleteConfirmDialog
  const [pendingDelete, setPendingDelete] = useState<NetworkType | null>(null);
  const [networkAvailability, setNetworkAvailability] = useState<Map<string, { modemCount: number; locationCount: number; isStale: boolean; locationIds: string[] }>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingNetwork, setEditingNetwork] = useState<NetworkType | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formMcc, setFormMcc] = useState('');
  const [formMnc, setFormMnc] = useState('');
  const [formCountry, setFormCountry] = useState('');
  const [formCountryCode, setFormCountryCode] = useState('');
  const [formLogo, setFormLogo] = useState('');

  // Load networks with real-time listener
  useEffect(() => {
    const q = query(
      collection(db, 'networks'),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const networksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: toDate(doc.data().createdAt) || new Date(),
        updatedAt: toDate(doc.data().updatedAt) || new Date(),
      })) as NetworkType[];
      setNetworks(networksData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching networks:', error);
      showError('Failed to load networks');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load network availability from edge devices
  useEffect(() => {
    loadNetworkAvailability();
  }, []);

  const loadNetworkAvailability = async () => {
    try {
      setLoadingAvailability(true);

      // Get all devices from central server
      const devices = await edgeDeviceService.getDevices();

      // Aggregate network availability from all devices
      const availabilityMap = new Map<string, { modemCount: number; locationCount: number; isStale: boolean; locationIds: string[]; modemIds: Set<string>; locationIdSet: Set<string> }>();
      const STALENESS_THRESHOLD_MS = 4 * 60 * 60 * 1000; // 4 hours
      const now = Date.now();

      for (const device of devices) {
        try {
          const networkData = await edgeDeviceService.getDeviceAvailableNetworks(device.id);

          for (const network of networkData.networks) {
            const key = `${network.mcc}-${network.mnc}`;
            const modemKey = network.modemId || device.id;
            const locationId = device.locationId || '';

            // Parse timestamp
            let timestamp: Date | undefined;
            if (network.timestamp) {
              if (network.timestamp._seconds) {
                timestamp = new Date(network.timestamp._seconds * 1000);
              } else if (network.timestamp.toDate) {
                timestamp = network.timestamp.toDate();
              } else {
                timestamp = new Date(network.timestamp);
              }
            }

            const isStale = timestamp ? (now - timestamp.getTime() > STALENESS_THRESHOLD_MS) : true;

            if (!availabilityMap.has(key)) {
              availabilityMap.set(key, {
                modemCount: 1,
                locationCount: locationId ? 1 : 0,
                isStale,
                locationIds: locationId ? [locationId] : [],
                modemIds: new Set([modemKey]),
                locationIdSet: locationId ? new Set([locationId]) : new Set()
              });
            } else {
              const existing = availabilityMap.get(key)!;
              if (!existing.modemIds.has(modemKey)) {
                existing.modemIds.add(modemKey);
                existing.modemCount = existing.modemIds.size;
              }
              if (locationId && !existing.locationIdSet.has(locationId)) {
                existing.locationIdSet.add(locationId);
                existing.locationIds = Array.from(existing.locationIdSet);
                existing.locationCount = existing.locationIdSet.size;
              }
              // Update staleness - only stale if ALL scans are stale
              if (!isStale) {
                existing.isStale = false;
              }
            }
          }
        } catch (err) {
          console.warn(`Failed to fetch networks for device ${device.id}:`, err);
        }
      }

      // Convert to simpler format for state (remove Sets)
      const finalMap = new Map<string, { modemCount: number; locationCount: number; isStale: boolean; locationIds: string[] }>();
      availabilityMap.forEach((value, key) => {
        finalMap.set(key, {
          modemCount: value.modemCount,
          locationCount: value.locationCount,
          isStale: value.isStale,
          locationIds: value.locationIds
        });
      });

      setNetworkAvailability(finalMap);
      console.log('[NetworksPage] Loaded availability for', finalMap.size, 'networks');
    } catch (err) {
      console.error('Failed to load network availability:', err);
    } finally {
      setLoadingAvailability(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormName('');
    setFormMcc('');
    setFormMnc('');
    setFormCountry('');
    setFormCountryCode('');
    setFormLogo('');
    setEditingNetwork(null);
  };

  // Open modal for editing
  const handleEdit = (network: NetworkType) => {
    setEditingNetwork(network);
    setFormName(network.name);
    setFormMcc(network.mcc);
    setFormMnc(network.mnc);
    setFormCountry(network.country);
    setFormCountryCode(network.countryCode);
    setFormLogo(network.logo || '');
    setShowModal(true);
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (!currentUser) return;

    if (!formName || !formMcc || !formMnc || !formCountry || !formCountryCode) {
      showError('Please fill in all required fields');
      return;
    }

    setFormSubmitting(true);
    try {
      if (editingNetwork) {
        // Update existing network
        await updateDoc(doc(db, 'networks', editingNetwork.id), {
          name: formName,
          mcc: formMcc,
          mnc: formMnc,
          country: formCountry,
          countryCode: formCountryCode,
          logo: formLogo || null,
          updatedAt: serverTimestamp(),
        });
        success('Network updated successfully');
      } else {
        // Create new network
        await addDoc(collection(db, 'networks'), {
          name: formName,
          mcc: formMcc,
          mnc: formMnc,
          country: formCountry,
          countryCode: formCountryCode,
          logo: formLogo || null,
          isGlobal: true,
          isActive: true,
          createdBy: currentUser.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        success('Network created successfully');
      }

      setShowModal(false);
      resetForm();
    } catch (err: any) {
      showError(err.message || 'Failed to save network');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle delete (soft delete)
  const handleDelete = async (network: NetworkType) => {
    try {
      await updateDoc(doc(db, 'networks', network.id), {
        isActive: false,
        updatedAt: serverTimestamp(),
      });
      success('Network deleted successfully');
    } catch (err: any) {
      showError(err.message || 'Failed to delete network');
    }
  };

  // Initialize data table filters
  const activeNetworks = networks.filter(n => n.isActive);

  // Build dynamic filter config with country options from data
  const filterColumnsConfig = useMemo(() => {
    // Get unique countries from networks
    const uniqueCountries = [...new Set(networks.map(n => n.country).filter(Boolean))].sort();

    return [
      ...getStaticFilterConfigs(),
      filterColumnHelper
        .option()
        .id('country')
        .accessor((row) => row.country)
        .displayName('Country')
        .icon(Flag)
        .options(uniqueCountries.map(c => ({ label: c, value: c })))
        .build(),
    ];
  }, [networks]);

  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: activeNetworks,
    columnsConfig: filterColumnsConfig,
  });

  // Apply filters
  const filteredNetworks = useMemo(() =>
    applyFilters(activeNetworks, filters, filterColumnsConfig),
    [activeNetworks, filters, filterColumnsConfig]
  );

  // Table columns
  const columns: Column<NetworkType>[] = [
    {
      key: 'name',
      header: 'Network',
      sortable: true,
      render: (_value: unknown, row: NetworkType) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent-blue rounded flex items-center justify-center">
            <Network className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-semibold">{row.name}</div>
            <div className="text-xs text-muted-foreground/70">
              MCC: {row.mcc} | MNC: {row.mnc}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'country',
      header: 'Country',
      sortable: true,
      render: (_value: unknown, row: NetworkType) => (
        <div className="flex items-center gap-2">
          <Flag className="w-4 h-4 text-muted-foreground/70" />
          <span>{row.country}</span>
          <Badge variant="secondary">{row.countryCode}</Badge>
        </div>
      ),
    },
    {
      key: 'isGlobal',
      header: 'Type',
      sortable: true,
      render: (_value: unknown, row: NetworkType) => (
        <Badge className={row.isGlobal ? 'bg-status-success-bg text-status-success' : ''} variant={row.isGlobal ? 'secondary' : 'secondary'}>
          {row.isGlobal ? (
            <>
              <Globe className="w-3 h-3 mr-1" />
              Global
            </>
          ) : (
            'Custom'
          )}
        </Badge>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      sortable: true,
      render: (_value: unknown, row: NetworkType) => (
        <Badge className={row.isActive ? 'bg-status-success-bg text-status-success' : ''} variant="secondary">
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'availability',
      header: 'Availability',
      sortable: false,
      render: (_value: unknown, row: NetworkType) => {
        const key = `${row.mcc}-${row.mnc}`;
        const availability = networkAvailability.get(key);

        if (!availability || availability.modemCount === 0) {
          return (
            <span className="text-muted-foreground/70 text-sm">No data</span>
          );
        }

        const handleViewLocations = (e: React.MouseEvent) => {
          e.stopPropagation();
          // Navigate to Location Analytics with locations filter
          if (availability.locationIds.length > 0) {
            navigate(`/admin/location-analytics?locations=${availability.locationIds.join(',')}`);
          } else {
            navigate('/admin/location-analytics');
          }
        };

        return (
          <button
            onClick={handleViewLocations}
            className="flex items-center gap-2 hover:text-primary transition-colors text-left"
            title="Click to view in Location Analytics"
          >
            <div className="flex items-center gap-1">
              <Signal className="w-3.5 h-3.5 text-muted-foreground/70" />
              <span className="text-sm">{availability.modemCount || 0}</span>
            </div>
            <span className="text-muted-foreground/50">at</span>
            <div className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground/70" />
              <span className="text-sm">{availability.locationCount || 0}</span>
            </div>
            {availability.isStale && (
              <span title="Stale data">
                <AlertCircle className="w-4 h-4 text-status-warning" />
              </span>
            )}
            <ExternalLink className="w-3 h-3 text-muted-foreground/50" />
          </button>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      render: (_, row) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row);
            }}
          >
            <Edit className="w-4 h-4" />
          </Button>
          {row.isActive && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setPendingDelete(row);
              }}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (!isSuperAdmin) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <p className="text-muted-foreground">
              You do not have permission to access this page.
            </p>
          </div>
        </div>
      </AppShell>
    );
  }

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-accent-blue mx-auto" />
            <p className="text-muted-foreground">Loading networks...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  const globalNetworks = networks.filter(n => n.isGlobal && n.isActive);

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">
              Network Management
            </h1>
            <p className="text-body text-muted-foreground">
              Manage global mobile network/carrier definitions for eSIM profiles
            </p>
          </div>

          <Button
            variant="default"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Network
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-caption text-muted-foreground uppercase mb-2">
                  Total Networks
                </div>
                <div className="text-display font-mono font-bold text-foreground">
                  {activeNetworks.length}
                </div>
              </div>
              <div className="w-12 h-12 bg-accent-blue/10 rounded flex items-center justify-center">
                <Network className="w-6 h-6 text-accent-blue" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-caption text-muted-foreground uppercase mb-2">
                  Global Networks
                </div>
                <div className="text-display font-mono font-bold text-accent-green">
                  {globalNetworks.length}
                </div>
              </div>
              <div className="w-12 h-12 bg-accent-green/10 rounded flex items-center justify-center">
                <Globe className="w-6 h-6 text-accent-green" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-caption text-muted-foreground uppercase mb-2">
                  Countries
                </div>
                <div className="text-display font-mono font-bold text-foreground">
                  {new Set(activeNetworks.map(n => n.countryCode)).size}
                </div>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                <Flag className="w-6 h-6 text-primary" />
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <DataTableFilter
            columns={filterColumns}
            filters={filters}
            actions={actions}
            strategy={strategy}
          />
        </Card>

        {/* Networks Table */}
        <Card className="p-6">
          <DataTable
            columns={columns}
            data={filteredNetworks}
            keyExtractor={(row) => row.id}
            paginated
            defaultPageSize={10}
            searchable
            searchPlaceholder="Search networks by name, MCC, MNC, or country..."
          />
        </Card>
      </div>

      {/* Add/Edit Network Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editingNetwork ? 'Edit Network' : 'Add Network'}
        description={editingNetwork ? 'Update network details' : 'Add a new global network'}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => {
              setShowModal(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSubmit}
              disabled={formSubmitting}
            >
              {formSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingNetwork ? 'Update' : 'Add'} Network
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="networkName">Network Name</Label>
            <Input
              id="networkName"
              placeholder="AT&T, O2 UK, Telefonica Spain, etc."
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="mcc">MCC (Mobile Country Code)</Label>
              <Input
                id="mcc"
                placeholder="310"
                value={formMcc}
                onChange={(e) => setFormMcc(e.target.value)}
                required
                maxLength={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mnc">MNC (Mobile Network Code)</Label>
              <Input
                id="mnc"
                placeholder="410"
                value={formMnc}
                onChange={(e) => setFormMnc(e.target.value)}
                required
                maxLength={3}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                placeholder="United States"
                value={formCountry}
                onChange={(e) => setFormCountry(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="countryCode">Country Code</Label>
              <Input
                id="countryCode"
                placeholder="US"
                value={formCountryCode}
                onChange={(e) => setFormCountryCode(e.target.value.toUpperCase())}
                required
                maxLength={2}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="logo">Logo URL (Optional)</Label>
            <Input
              id="logo"
              placeholder="https://example.com/logo.png"
              value={formLogo}
              onChange={(e) => setFormLogo(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      <DeleteConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        itemName={pendingDelete ? `${pendingDelete.name} (${pendingDelete.mcc}-${pendingDelete.mnc})` : undefined}
        details="The network is marked inactive and hidden from this list."
        onConfirm={async () => {
          if (pendingDelete) await handleDelete(pendingDelete);
        }}
      />
    </AppShell>
  );
}

export default NetworksPage;
